import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { ReportTypeOption, ReportType, ReportItem } from '../types';
import { getReports, downloadReportById } from '../services/api';
import { accent } from '../theme';

const REPORT_TYPES: ReportTypeOption[] = [
  {
    value: 'coral_compliance',
    label: 'Compliance Report',
    description: 'Comprehensive coral tracking and status report for regulatory compliance',
  },
];

const REPORTS_PAGE_SIZE = 50;

// Helper function to format date for input[type="date"]
const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to format date for display
const formatDateForDisplay = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Helper function to format datetime for display
const formatDateTimeForDisplay = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('en-AU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ViewReports: React.FC = () => {
  const [reports, setReports] = useState<ReportItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingReport, setDownloadingReport] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [cursorHistory, setCursorHistory] = useState<(string | null)[]>([]);
  const [currentCursor, setCurrentCursor] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);

  // Search filters
  const [reportType, setReportType] = useState<ReportType>('coral_compliance');
  const [dateFrom, setDateFrom] = useState<string>(
    formatDateForInput(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)) // 30 days ago
  );
  const [dateTo, setDateTo] = useState<string>(formatDateForInput(new Date()));

  const downloadReport = async (report: ReportItem): Promise<void> => {
    console.log('Downloading report:', report.reportId);

    const downloadData = await downloadReportById(report.reportId);
    const extension = report.format?.toLowerCase() || 'csv';

    const response = await fetch(downloadData.downloadUrl);
    if (!response.ok) {
      throw new Error('Failed to fetch report file');
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = `coral_report_${report.reportId}.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  };

  const loadReports = useCallback(
    async ({
      cursor = null,
      resetHistory = false,
    }: { cursor?: string | null; resetHistory?: boolean } = {}) => {
      setLoading(true);
      setError(null);
      setDownloadError(null);

      let success = false;

      try {
        const response = await getReports(
          reportType,
          cursor ?? undefined,
          REPORTS_PAGE_SIZE,
          dateFrom,
          dateTo
        );
        setReports(response.items || []);
        setNextCursor(response.nextCursor || null);
        setCurrentCursor(cursor ?? null);

        if (resetHistory) {
          setCursorHistory([]);
          setPageNumber(1);
        }

        success = true;
      } catch (err: any) {
        console.error('Error loading reports:', err);
        setError(err.message || 'Failed to load reports');
        setReports([]);
        setNextCursor(null);
      } finally {
        setLoading(false);
      }

      return success;
    },
    [reportType, dateFrom, dateTo]
  );

  // Auto-refresh when report type changes; date filters require pressing Search to avoid churn while editing.
  useEffect(() => {
    loadReports({ resetHistory: true });
  }, [reportType, loadReports]);

  const handleRefresh = () => {
    loadReports({ resetHistory: true });
  };

  const handleNextPage = async () => {
    if (!nextCursor || loading) return;

    const success = await loadReports({ cursor: nextCursor });

    if (success) {
      setCursorHistory((prev) => [...prev, currentCursor]);
      setPageNumber((prev) => prev + 1);
    }
  };

  const handlePrevPage = async () => {
    if (cursorHistory.length === 0 || loading) return;

    const updatedHistory = [...cursorHistory];
    const prevCursor = updatedHistory.pop() ?? null;

    const success = await loadReports({ cursor: prevCursor });

    if (success) {
      setCursorHistory(updatedHistory);
      setPageNumber((prev) => Math.max(1, prev - 1));
    }
  };

  const handleDownload = async (report: ReportItem) => {
    if (report.status !== 'completed') {
      setDownloadError('Report is not ready for download');
      return;
    }

    setDownloadingReport(report.reportId);
    setDownloadError(null);

    try {
      await downloadReport(report);
    } catch (err: any) {
      setDownloadError(err.message || 'Failed to download report');
    } finally {
      setDownloadingReport(null);
    }
  };

  const handleSearch = () => {
    loadReports({ resetHistory: true });
  };

  const getStatusChip = (status: string) => {
    const statusConfig = {
      completed: { color: 'success' as const, label: 'Completed' },
      processing: { color: 'warning' as const, label: 'Processing' },
      pending: { color: 'info' as const, label: 'Pending' },
      failed: { color: 'error' as const, label: 'Failed' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || {
      color: 'default' as const,
      label: status,
    };

    return <Chip label={config.label} color={config.color} size="small" />;
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        View Reports
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        View and download previously generated reports. Use the search filters to find specific
        reports.
      </Typography>

      {/* Search Filters */}
      <Paper sx={{ p: 3, mb: 3, ...accent.coral }}>
        <Typography variant="h6" gutterBottom>
          Search Filters
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="report-type-label">Report Type</InputLabel>
            <Select
              labelId="report-type-label"
              value={reportType}
              label="Report Type"
              onChange={(e) => setReportType(e.target.value as ReportType)}
            >
              {REPORT_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="From Date"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />

          <TextField
            label="To Date"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />

          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
            sx={{ minHeight: 56 }}
          >
            {loading ? 'Loading...' : 'Search'}
          </Button>

          <Tooltip title="Refresh current results">
            <IconButton onClick={handleRefresh} disabled={loading} sx={{ minHeight: 56 }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Download Error Alert */}
      {downloadError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setDownloadError(null)}>
          {downloadError}
        </Alert>
      )}

      {/* Reports Table */}
      <Paper sx={accent.navy}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Report ID</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Date Range</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Loading reports...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : reports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No reports found. Try adjusting your search filters or generate a new report.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                reports.map((report) => (
                  <TableRow key={report.reportId} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {report.reportId.substring(0, 16)}...
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {REPORT_TYPES.find((t) => t.value === report.reportType)?.label ||
                        report.reportType}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateForDisplay(report.dateFrom)} -{' '}
                        {formatDateForDisplay(report.dateTo)}
                      </Typography>
                    </TableCell>
                    <TableCell>{getStatusChip(report.status)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateTimeForDisplay(report.createdDate)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleDownload(report)}
                        disabled={
                          report.status !== 'completed' || downloadingReport === report.reportId
                        }
                        startIcon={
                          downloadingReport === report.reportId ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DownloadIcon />
                          )
                        }
                      >
                        {downloadingReport === report.reportId ? 'Downloading...' : 'Download'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 3,
            py: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={handlePrevPage}
            disabled={loading || pageNumber === 1}
            size="small"
          >
            Previous Page
          </Button>
          <Typography variant="body2" color="text.secondary">
            {reports.length > 0
              ? `Showing ${(pageNumber - 1) * REPORTS_PAGE_SIZE + 1}-${(pageNumber - 1) * REPORTS_PAGE_SIZE + reports.length} (Page ${pageNumber})`
              : `Page ${pageNumber}`}
          </Typography>
          <Button
            variant="outlined"
            onClick={handleNextPage}
            disabled={loading || !nextCursor}
            size="small"
          >
            Next Page
          </Button>
        </Box>
      </Paper>

      {/* Help Information */}
      <Paper sx={{ p: 3, bgcolor: 'grey.50', mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          About Report Downloads
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          This page shows all previously generated reports that are available for download. Reports
          are automatically cleaned up after a certain period for security and storage efficiency.
        </Typography>

        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
          Report Status Meanings:
        </Typography>
        <Box
          component="ul"
          sx={{
            margin: 0,
            paddingLeft: '20px',
            listStyle: 'none',
          }}
        >
          <Box component="li" sx={{ mb: 1 }}>
            <Typography variant="body2">
              <strong>Completed:</strong> Report is ready for download
            </Typography>
          </Box>
          <Box component="li" sx={{ mb: 1 }}>
            <Typography variant="body2">
              <strong>Processing:</strong> Report is being generated
            </Typography>
          </Box>
          <Box component="li" sx={{ mb: 1 }}>
            <Typography variant="body2">
              <strong>Pending:</strong> Report is queued for generation
            </Typography>
          </Box>
          <Box component="li" sx={{ mb: 1 }}>
            <Typography variant="body2">
              <strong>Failed:</strong> Report generation encountered an error
            </Typography>
          </Box>
        </Box>

        <Typography variant="caption" display="block" sx={{ mt: 2, fontStyle: 'italic' }}>
          Note: If a download fails with an &ldquo;expired link&rdquo; error, simply click the
          Download button again to request a fresh secure link for the same report.
        </Typography>
      </Paper>
    </Box>
  );
};

export default ViewReports;
